import { useState } from 'react';
import { Modal, ModalBody, Button, Stack } from '@7shifts/sous-chef-base';
import type { ClockRole } from '@src/api/punchTypes';
import { useTranslation } from '@src/i18n';
import { PickerList, PickerCell } from './PickerCell';

interface OfflineRoleSelectionModalProps {
    roles: ClockRole[];
    onSelect: (roleId: number, departmentId: number | null) => void;
    onCancel: () => void;
}

interface RoleGroup {
    departmentId: number | null;
    departmentName: string | null;
    roles: ClockRole[];
}

// Groups roles by department, preserving the order departments/roles first
// appear in — mirrors the Figma design ("FRONT OF HOUSE" / "BACK OF HOUSE"
// sections). Roles without a department are grouped together with no title.
function groupRolesByDepartment(roles: ClockRole[]): RoleGroup[] {
    const groups: RoleGroup[] = [];
    const groupsByKey = new Map<string, RoleGroup>();

    for (const role of roles) {
        const key = role.departmentId === null ? '' : String(role.departmentId);
        let group = groupsByKey.get(key);
        if (!group) {
            group = {
                departmentId: role.departmentId,
                departmentName: role.departmentName,
                roles: []
            };
            groupsByKey.set(key, group);
            groups.push(group);
        }
        group.roles.push(role);
    }

    return groups;
}

export function OfflineRoleSelectionModal({
    roles,
    onSelect,
    onCancel
}: OfflineRoleSelectionModalProps) {
    const [submitted, setSubmitted] = useState(false);
    const { t } = useTranslation();

    const groups = groupRolesByDepartment(roles);

    function handleSelect(role: ClockRole) {
        if (submitted) return;
        setSubmitted(true);
        onSelect(role.id, role.departmentId);
    }

    return (
        <Modal header={t('role.selectTitle')} testId="offline-role-selection-modal">
            <ModalBody>
                <Stack space={16}>
                    {groups.map(group => (
                        <PickerList
                            key={group.departmentId ?? 'none'}
                            title={group.departmentName ?? undefined}
                            testId={`offline-role-group-${group.departmentId ?? 'none'}`}
                        >
                            {group.roles.map(role => (
                                <PickerCell
                                    key={role.id}
                                    label={role.name}
                                    color={role.color}
                                    disabled={submitted}
                                    onSelect={() => handleSelect(role)}
                                    testId={`offline-role-selection-${role.id}`}
                                />
                            ))}
                        </PickerList>
                    ))}
                    <Button
                        type="button"
                        theme="hollow"
                        size="full-width"
                        disabled={submitted}
                        onClick={onCancel}
                        testId="offline-role-selection-cancel"
                    >
                        {t('common.cancel')}
                    </Button>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
